import { useNavigate } from 'react-router-dom';
import useUpdateEffect from 'src/shared/helpers/useUpdateEffect';

const useRedirectFromCreate = (result: any, editId: any, editIdKey: string, newUrl: string, options: RedirectOptions = {}) => {
  const navigate = useNavigate();
  useUpdateEffect(() => {
    const { replacementIdKey, replacementUrl, openReplacementInNewTab = false, openReplacementInSameTab = false, replacementPredicate, reloadAfterNavigate = true, } = options;

    // Handle "No Content" shortcut
    if (result === "No Content") {
      navigate(newUrl);
      return;
    }
    if (!result || typeof result !== "object") return;

    // ---------------------------------------------------------
    // Replacement order detection + open
    // ---------------------------------------------------------
    const isReplacement =
      typeof replacementPredicate === "function"
        ? replacementPredicate(result)
        : Boolean(
          replacementIdKey &&
          replacementUrl &&
          result?.[replacementIdKey] != null &&
          String(result?.[replacementIdKey]).length > 0 &&
          // heuristic: replacement response looks like a SalesOrder payload
          (result?.Status?.Value === "Draft" || result?.SalesOrderRows != null || result?.SalesOrderId != null)
        );
    if (isReplacement && replacementIdKey && replacementUrl) {
      const replacementId = result?.[replacementIdKey];
      if (openReplacementInNewTab) {
        window.open(replacementUrl + replacementId, "_blank", "noopener,noreferrer");
      } else if (openReplacementInSameTab) {
        navigate(replacementUrl + replacementId);
        if (reloadAfterNavigate) navigate(0);
      }
      // continue; we can still also redirect the return order below if present
    }

    // ---------------------------------------------------------
    // Original create -> edit redirect (Return Order)
    // ---------------------------------------------------------
    const hasEditId = result?.[editIdKey] != null && String(result?.[editIdKey]).length > 0;
    if (editId == null && hasEditId) {
      navigate(newUrl + result?.[editIdKey]);
      if (reloadAfterNavigate) navigate(0);
    }
  }, [result]);
};

export default useRedirectFromCreate;

type RedirectOptions = {
  // replacement flow
  replacementIdKey?: string;                // e.g. "SalesOrderId" or "ReplacementSalesOrderId"
  replacementUrl?: string;                  // e.g. "/orders/sales/edit?id="
  openReplacementInNewTab?: boolean;        // default false
  openReplacementInSameTab?: boolean;       // default false (if true, uses navigate instead)
  replacementPredicate?: (result: any) => boolean; // optional custom check

  // existing behavior
  reloadAfterNavigate?: boolean;            // default true (your current behavior)
};
